// Curated mapping for the redesigned homepage (home-v2). Everything here is keyed by
// Category id and SubCategory id from the live catalog (see HOMEPAGE_REDESIGN_ANALYSIS.md
// section 4). Names, slugs and the sub-category lists themselves come from GraphQL at
// request time; an id that is missing or inactive in the current database is skipped,
// so dev/prod data differences never produce a dead link.

// Category tabs, in the mockup's order. Each tab draws tasks from one or more categories:
// `prefer` lists sub-category ids to show first (mockup tasks), `max` caps how many tasks
// that category contributes in the first pass. The first category present also provides
// the "See all … services" link.
export const TABS = [
  { key: 'assembly', label: 'Assembly', icon: 'box', categories: [{ id: 10, prefer: [49, 134, 132, 135, 136, 138] }] },
  {
    key: 'mounting',
    label: 'Mounting',
    icon: 'tv',
    categories: [
      { id: 12, prefer: [12], max: 1 },
      { id: 13, prefer: [117, 119, 115, 120, 116] },
    ],
  },
  { key: 'plumbing', label: 'Plumbing', icon: 'drop', categories: [{ id: 2, prefer: [13, 1, 95, 15, 97, 3] }] },
  {
    key: 'electrical',
    label: 'Electrical',
    icon: 'bolt',
    categories: [
      { id: 1, prefer: [11, 10, 83, 8, 86], max: 5 },
      { id: 36, prefer: [213, 215], max: 1 },
    ],
  },
  {
    key: 'cleaning',
    label: 'Cleaning',
    icon: 'spark',
    categories: [
      { id: 4, prefer: [21, 106, 107, 24], max: 4 },
      { id: 29, prefer: [194, 73], max: 2 },
    ],
  },
  {
    key: 'moving',
    label: 'Moving',
    icon: 'truck',
    categories: [
      { id: 14, prefer: [40, 41, 149, 39], max: 4 },
      { id: 15, prefer: [46, 153], max: 2 },
    ],
  },
  { key: 'yard', label: 'Yard work', icon: 'leaf', categories: [{ id: 18, prefer: [59, 61, 60, 58, 158] }] },
  {
    key: 'painting',
    label: 'Painting',
    icon: 'brush',
    categories: [
      { id: 5, prefer: [34, 35, 33], max: 4 },
      { id: 20, prefer: [164, 63], max: 1 },
      { id: 23, prefer: [67], max: 1 },
    ],
  },
];

export const MAX_TASKS_PER_TAB = 6;

// Every category id the tabs may need sub-categories for.
export const TAB_CATEGORY_IDS = Array.from(new Set(TABS.flatMap((t) => t.categories.map((c) => c.id))));

// Hero "popular task" chips: marketing labels, each opening its category's booking page.
export const CHIPS = [
  { label: 'TV mounting', categoryId: 12 },
  { label: 'Furniture assembly', categoryId: 10 },
  { label: 'Leaky faucet', categoryId: 2 },
  { label: 'Light fixture', categoryId: 1 },
  { label: 'Home cleaning', categoryId: 4 },
  { label: 'Help moving', categoryId: 14 },
];

// Popular project cards. Images are the optimised copies in public/images/home/.
// Hero photo rotation. Same mechanic as the old homepage banner (2-layer crossfade, 10 s,
// paused while the tab is hidden, static for prefers-reduced-motion). `theme: 'renovation'`
// slides are dropped when Large Projects is off, so the hero never suggests a service we are
// not selling (small jobs ≠ Large Projects). The first slide is the one shipped with home-v2.
export const HERO_SLIDES = [
  { image: 'hero-pro-electrical', width: 1000, height: 1050, alt: 'A pro testing the wiring in an electrical panel', theme: 'quick' },
  { image: 'hero-electrical-installation', width: 562, height: 1000, alt: 'An electrical panel and light fixtures being installed by a pro', theme: 'quick' },
  { image: 'hero-hvac-installation', width: 1000, height: 710, alt: 'A newly installed HVAC system with indoor unit and outdoor condenser', theme: 'quick' },
  { image: 'hero-kitchen-renovation', width: 1000, height: 667, alt: 'A freshly renovated modern kitchen with new cabinets and an island', theme: 'renovation' },
  { image: 'hero-bathroom-renovation', width: 1000, height: 667, alt: 'A renovated bathroom with a new vanity, tiled walls and a freestanding tub', theme: 'renovation' },
  { image: 'hero-porch-renovation', width: 1000, height: 667, alt: 'A refreshed front porch with new columns, railing and a red front door', theme: 'quick' },
];
export const HERO_INTERVAL_MS = 10000;

export const PROJECTS = [
  {
    title: 'TV mounting',
    text: 'Wall mount, any screen size',
    categoryId: 12,
    image: 'project-tv-mounting',
    width: 800,
    height: 600,
    alt: 'Two pros lifting a flat-screen TV onto a wall mount',
  },
  {
    title: 'Furniture assembly',
    text: 'Beds, desks, shelves',
    categoryId: 10,
    image: 'project-furniture-assembly',
    width: 512,
    height: 384,
    alt: 'A BestHand pro assembling a white cabinet',
  },
  {
    title: 'Clogged drain',
    text: 'Sinks, tubs, toilets',
    categoryId: 2,
    image: 'project-clogged-drain',
    width: 800,
    height: 600,
    alt: 'A plumber working on the pipes under a kitchen sink',
  },
  {
    title: 'Light fixture install',
    text: 'New or replacement',
    categoryId: 1,
    image: 'project-light-fixture',
    width: 800,
    height: 600,
    alt: 'An electrician installing a light fixture',
  },
];

// BestHand customer app store listings (same URLs as the current homepage and footer).
export const CLIENT_APP_STORE = 'https://apps.apple.com/us/app/besthand/id6759716088';
export const CLIENT_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.worldtechnologiessolutionsllc.besthand&hl=en_US';
